import { Info, Search, X } from "lucide-react";
import { useState, type JSX, type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useCan } from "@/shared/auth";
import { PageHeader } from "@/shared/components/PageHeader";
import { Pagination } from "@/shared/components/Pagination";
import { toast } from "@/shared/lib/toast";

import { useListRatio } from "../api/use-list-ratio";
import { useRatioPolicy } from "../api/use-ratio-policy";
import { RatioEditorRow } from "../components/RatioEditorRow";

const PAGE_LIMIT = 50;

function Th({ children }: { children: ReactNode }): JSX.Element {
  return (
    <th className="h-11 px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap select-none">
      {children}
    </th>
  );
}

function RatioSkeleton(): JSX.Element {
  return (
    <div className="space-y-2 p-4" data-testid="ratio-skeleton">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-[52px] animate-pulse rounded-[10px] bg-[var(--surface-sunken)]" />
      ))}
    </div>
  );
}

export function InventoryRatioPage({ initialSku = "" }: { initialSku?: string }): JSX.Element {
  const { t } = useTranslation(["inventory", "nav", "common"]);
  const canWrite = useCan("stock:write");
  const [offset, setOffset] = useState(0);
  const [query, setQuery] = useState(initialSku);
  const [activeSku, setActiveSku] = useState(initialSku);

  const list = useListRatio({ offset, limit: PAGE_LIMIT });
  const searching = activeSku.length > 0;
  const lookup = useRatioPolicy(activeSku, searching);

  const total = list.data?.total ?? 0;
  const isLoading = searching ? lookup.isLoading : list.isLoading;
  const isError = searching ? lookup.isError : list.isError;
  const rows = searching ? (lookup.data ? [lookup.data] : []) : (list.data?.items ?? []);

  function submitSearch(): void {
    setActiveSku(query.trim());
  }

  function clearSearch(): void {
    setQuery("");
    setActiveSku("");
  }

  function onSearchKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "Enter") {
      e.preventDefault();
      submitSearch();
    }
  }

  return (
    <div data-testid="inventory-ratio-page">
      <PageHeader
        breadcrumb={
          <>
            <span>{t("nav:group.operations")}</span>
            <span className="text-[var(--premium-border-strong)]">/</span>
            <span className="text-[var(--text-secondary)]">{t("nav:item.inventory_ratio")}</span>
          </>
        }
        title={t("ratio.page.title")}
        subtitle={t("ratio.page.subtitle", { count: total })}
        actions={
          canWrite ? (
            <button
              type="button"
              onClick={() => toast.info(t("ratio.toast.import_soon"))}
              data-testid="ratio-import"
              className="inline-flex h-[var(--control-h)] items-center gap-1.5 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-4 text-[13.5px] font-semibold text-[var(--text-secondary)] shadow-[var(--sh-xs)] transition hover:border-[#c7cdd9] hover:bg-[var(--canvas)] focus-ring"
            >
              {t("ratio.import")}
            </button>
          ) : undefined
        }
      />

      <div
        className="mb-4 flex items-center gap-2 rounded-[var(--control-r)] border border-[var(--brand-100)] bg-[var(--brand-50)] px-3.5 py-2.5 text-[13px] text-[var(--brand-700)]"
        data-testid="ratio-sync-notice"
      >
        <Info className="h-4 w-4 shrink-0" aria-hidden />
        {t("ratio.sync_notice")}
      </div>

      <div className="mb-4 flex items-center gap-2.5">
        <div className="relative min-w-[220px] max-w-md flex-1">
          <Search
            className="pointer-events-none absolute left-[13px] top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-[var(--text-subtle)]"
            aria-hidden
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder={t("ratio.search_placeholder")}
            data-testid="ratio-search"
            className="sku h-[var(--control-h)] w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white pl-[38px] pr-9 text-sm outline-none transition focus:border-[var(--brand-500)] focus-ring"
            style={{ boxShadow: "var(--sh-xs)" }}
          />
          {query && (
            <button
              type="button"
              onClick={clearSearch}
              aria-label={t("common:action.clear")}
              data-testid="ratio-search-clear"
              className="absolute right-2.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md text-[var(--text-subtle)] transition hover:bg-[var(--canvas)] focus-ring"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      </div>

      {isError ? (
        <div
          className="rounded-[14px] border border-[var(--premium-border)] bg-white py-16 text-center text-sm text-[var(--text-primary)] shadow-[var(--sh-card)]"
          data-testid={searching ? "ratio-search-empty" : "ratio-error"}
        >
          {searching ? t("ratio.search_not_found", { sku: activeSku }) : t("state.error")}
        </div>
      ) : isLoading ? (
        <div
          className="rounded-[14px] border border-[var(--premium-border)] bg-white shadow-[var(--sh-card)]"
          data-testid="ratio-loading"
        >
          <RatioSkeleton />
        </div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-[14px] border border-[var(--premium-border)] bg-white py-16 text-center shadow-[var(--sh-card)]"
          data-testid={searching ? "ratio-search-empty" : "ratio-empty"}
        >
          <p className="text-sm font-medium text-[var(--text-primary)]">
            {searching ? t("ratio.search_not_found", { sku: activeSku }) : t("ratio.state.empty")}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-[14px] border border-[var(--premium-border)] shadow-[var(--sh-card)]">
            <table className="min-w-full border-collapse" data-testid="ratio-table">
              <thead className="border-b border-[var(--row-divider)] bg-white">
                <tr>
                  <Th>{t("ratio.column.sku")}</Th>
                  <Th>{t("ratio.shopee")}</Th>
                  <Th>{t("ratio.lazada")}</Th>
                  <Th>{t("ratio.tiktok")}</Th>
                  <Th>{t("ratio.column.sum")}</Th>
                  <Th>{t("ratio.column.actions")}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--row-divider)]">
                {rows.map((p, i) => (
                  <RatioEditorRow key={p.sku ?? i} policy={p} readOnly={!canWrite} />
                ))}
              </tbody>
            </table>
          </div>
          {!searching && (
            <Pagination
              total={total}
              offset={offset}
              limit={PAGE_LIMIT}
              onChange={({ offset: next }) => setOffset(next)}
            />
          )}
        </>
      )}
    </div>
  );
}
